// Proof of concept: dual-ship Bitsburg browser RUM + Logs to a second Datadog org.
// The Browser SDK can only send a session to one org, so sessions go to DEFAULT_ORG_ID unless
// opted in with ?ship=<orgId> (e.g. https://bitsburg.io/?ship=2069027). Real users keep
// seeing their data in 11287; ?ship=11287 switches back.
// The opt-in is kept in localStorage until the RUM max session length (4h) after the
// last page load, so a session never flips orgs mid-way (both orgs share the same
// _dd_s session cookie). Regular use keeps the opt-in alive; ?ship=11287 ends it.

export interface RumOrgConfig {
  orgId: string;
  clientToken: string;
  applicationId: string;
  site: string;
}

export type RumOrgStorage = Pick<Storage, 'getItem' | 'setItem'>;

export const DEFAULT_ORG_ID = '11287';
const OTHER_ORG_ID = '2069027';

export const RUM_ORGS: Record<string, RumOrgConfig> = {
  // Datadog Demo
  [DEFAULT_ORG_ID]: {
    orgId: DEFAULT_ORG_ID,
    clientToken: 'pub3566a93e47a31cd7dff940baa9650117',
    applicationId: 'f683ebc4-9e80-4031-91ac-f0ae438be56c',
    site: 'datadoghq.com',
  },
  // Opt-in only, via ?ship=2069027
  [OTHER_ORG_ID]: {
    orgId: OTHER_ORG_ID,
    clientToken: 'pub695d92055cf1dcf758dbae32f2489041',
    applicationId: 'c8f73d1a-4a05-4b55-8430-aeb57d23079c',
    site: 'datadoghq.com',
  },
};

const STORAGE_KEY = 'bitsburg_rum_org';
// RUM sessions last at most 4 hours
const ASSIGNMENT_TTL_MS = 4 * 60 * 60 * 1000;

const isConfigured = (org?: RumOrgConfig): org is RumOrgConfig => !!org?.clientToken && !!org?.applicationId;

function readStored(storage: RumOrgStorage | null, now: number): string | null {
  try {
    const raw = storage?.getItem(STORAGE_KEY);
    if (!raw) return null;
    const { orgId, expiresAt } = JSON.parse(raw);
    return typeof orgId === 'string' && expiresAt > now ? orgId : null;
  } catch {
    return null;
  }
}

function writeStored(storage: RumOrgStorage | null, orgId: string, now: number) {
  try {
    storage?.setItem(STORAGE_KEY, JSON.stringify({ orgId, expiresAt: now + ASSIGNMENT_TTL_MS }));
  } catch {
    // Storage blocked: the org is re-resolved on the next page load
  }
}

export function resolveRumOrg({
  search,
  storage,
  now = Date.now(),
  orgs = RUM_ORGS,
}: {
  search: string;
  storage: RumOrgStorage | null;
  now?: number;
  orgs?: Record<string, RumOrgConfig>;
}): RumOrgConfig {
  const shipParam = new URLSearchParams(search).get('ship');
  const stored = readStored(storage, now);

  let org: RumOrgConfig | undefined;
  if (shipParam && isConfigured(orgs[shipParam])) {
    org = orgs[shipParam];
  } else if (stored && isConfigured(orgs[stored])) {
    org = orgs[stored];
  }
  org ??= orgs[DEFAULT_ORG_ID];

  // Sliding expiry: refreshed on every load, see the header comment
  writeStored(storage, org.orgId, now);
  return org;
}

function browserStorage(): RumOrgStorage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export const getRumOrg = () => resolveRumOrg({ search: window.location.search, storage: browserStorage() });
